import { CheckCircle2, X } from "lucide-react";

import CompositeDonut from "./CompositeDonut";
import { TierPill } from "./EngagementBadges";
import {
  evidenceComplete,
  isPoll1EvidenceDateInBounds,
  poll1EvidenceDateBounds,
  poll1Lookup,
  poll1RawFromAnswers,
  poll1ScoringScaleRows,
  type Poll1Question,
  type Poll1Tier,
} from "./poll1Helpers";
import Button from "../../Components/Button";
import DatePicker from "../../Components/DatePicker";
import CommonModal from "../../Components/Modal";
import {
  MCI_MUTED,
  notificationBannerClass,
} from "../../Utils/Helper/sponsorEngagementUi";

type Account = {
  account_name: string;
  theater?: string;
  region?: string;
  /** ``primary`` | ``secondary`` for this sponsor on the account */
  sponsor_role?: "primary" | "secondary" | string | null;
  poll1_submitted_at?: string | null;
  poll1_evidence?: Record<string, string> | null;
};

const sponsorRoleLabel = (role?: string | null): string | null => {
  if (role === "primary") return "Primary Sponsor";
  if (role === "secondary") return "Secondary Sponsor";
  return null;
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  account: Account | null;
  period: string;
  cycleName?: string;
  questions: Poll1Question[];
  poll1Tiers: Poll1Tier[];
  showRubric: boolean;
  answers: (boolean | null)[];
  evidence: Record<string, string>;
  notes: string;
  onAnswersChange: (answers: (boolean | null)[]) => void;
  onEvidenceChange: (evidence: Record<string, string>) => void;
  onNotesChange: (notes: string) => void;
  onSaveDraft: () => void;
  onSubmit: () => void;
  submitting?: boolean;
  savingDraft?: boolean;
};

const CheckInModal = ({
  isOpen,
  onClose,
  account,
  period,
  cycleName,
  questions,
  poll1Tiers,
  showRubric,
  answers,
  evidence,
  notes,
  onAnswersChange,
  onEvidenceChange,
  onNotesChange,
  onSaveDraft,
  onSubmit,
  submitting = false,
  savingDraft = false,
}: Props) => {
  if (!account) return null;

  const readOnly = Boolean(account.poll1_submitted_at);
  const score = poll1RawFromAnswers(answers, questions);
  const tierObj = poll1Lookup(score, poll1Tiers);
  const scoringScaleRows = poll1ScoringScaleRows(poll1Tiers);
  const allAnswered = answers.every(a => a !== null);
  const evidenceOk = evidenceComplete(answers, evidence, questions, period);
  const canSubmit = allAnswered && evidenceOk && !readOnly && !submitting;
  const answeredCount = answers.filter(a => a !== null).length;
  const progressPercent =
    questions.length > 0
      ? Math.round((answeredCount / questions.length) * 100)
      : 0;

  const chips = Array.from(
    new Set(
      [
        period,
        account.theater,
        account.region,
        sponsorRoleLabel(account.sponsor_role),
      ].filter(Boolean) as string[]
    )
  );

  const setAnswer = (index: number, value: boolean) => {
    const next = [...answers];
    next[index] = value;
    onAnswersChange(next);
    if (!value) {
      const q = questions[index];
      if (q && evidence[q.id]) {
        const ev = { ...evidence };
        delete ev[q.id];
        onEvidenceChange(ev);
      }
    }
  };

  const setEvidence = (qid: string, value: string) => {
    onEvidenceChange({ ...evidence, [qid]: value });
  };

  return (
    <CommonModal
      isOpen={isOpen}
      toggle={onClose}
      size="5xl"
      showHeader={false}
      contentClassName="overflow-hidden flex flex-col"
      usePortal
    >
      {/* ── Modal Header ── */}
      <header className="flex-shrink-0 flex items-start justify-between gap-4 border-b border-gray-200 pb-4">
        <div className="min-w-0 flex-1">
          <h2 className="text-xl font-bold text-gray-900 leading-tight truncate">
            {cycleName
              ? `${cycleName} (${account.account_name})`
              : account.account_name}
          </h2>
          <div className="flex flex-wrap gap-1.5 mt-2">
            {chips.map(chip => (
              <span
                key={chip}
                className="rounded-full border border-gray-200 bg-gray-50 px-2.5 py-0.5 text-xs font-medium text-gray-600"
              >
                {chip}
              </span>
            ))}
          </div>
        </div>

        <div className="flex items-start gap-3 flex-shrink-0">
          <div className="rounded-lg bg-gray-100 border border-gray-200 px-3 pt-2 pb-3 text-center min-w-[190px]">
            <div className="text-sm font-semibold text-gray-800">
              {answeredCount} of {questions.length} answered
            </div>
            <div className="flex items-center gap-2 mt-1.5">
              <div className="flex-1 h-1.5 overflow-hidden rounded-full bg-gray-300">
                <div
                  className="h-full rounded-full bg-purple-600 transition-all duration-300"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
              <span className="text-[10px] font-semibold text-gray-500 shrink-0">
                {progressPercent}%
              </span>
            </div>
          </div>
          <button
            type="button"
            className="mt-0.5 p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
            onClick={onClose}
            aria-label="Close"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>
      </header>

      {/* ── Scrollable Body ── */}
      <section className="flex-1 min-h-0 overflow-y-auto mt-4">
        <div className="grid grid-cols-[minmax(0,1fr)_272px] gap-5 items-start max-[860px]:grid-cols-1">
          {/* Left: Questions panel */}
          <div className="min-w-0 rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
            {/* Questions header */}
            <div className="border-b border-gray-100 px-5 py-4 bg-gray-50/50">
              <div>
                <h3 className="text-sm font-bold text-gray-900">
                  Check-in questions
                </h3>
                <p className={`mt-0.5 text-xs ${MCI_MUTED}`}>
                  Answer each item and add required evidence where requested.
                </p>
              </div>
            </div>

            {/* Questions list */}
            <div className="p-5">
              {readOnly ? (
                <div className={`${notificationBannerClass("green")} mb-4`}>
                  <CheckCircle2 className="shrink-0" size={18} />
                  <div className="min-w-0">
                    <div className="font-bold text-[13px] mb-px">Submitted</div>
                    <div className="text-xs opacity-90">
                      Your responses for {period} are read-only. Edit window
                      closes at quarter end.
                    </div>
                  </div>
                </div>
              ) : null}

              <div className="flex flex-col gap-3">
                {questions.map((q, i) => {
                  const ans = answers[i];
                  const yes = ans === true;
                  const no = ans === false;
                  const showEvidence =
                    yes && q.evidence_type && q.evidence_type !== "none";
                  const val = evidence[q.id] ?? "";
                  const hasValue = !!String(val).trim();
                  const required = !!q.evidence_required;
                  const isDateEvidence = q.evidence_type === "date";
                  const dateBounds = isDateEvidence
                    ? poll1EvidenceDateBounds(q.id, period)
                    : null;
                  const dateOutOfBounds =
                    isDateEvidence &&
                    hasValue &&
                    !isPoll1EvidenceDateInBounds(val, dateBounds);
                  const missing = required && !hasValue && !readOnly;
                  const dateError = !readOnly
                    ? missing
                      ? "Required when answering Yes."
                      : dateOutOfBounds
                        ? (dateBounds?.errorMessage ?? "Select a valid date.")
                        : undefined
                    : undefined;
                  const inputType = q.evidence_type === "link" ? "url" : "text";

                  return (
                    <div
                      key={q.id}
                      className="rounded-xl border border-gray-200 bg-white p-4 transition-all hover:border-gray-300 hover:shadow-sm"
                    >
                      <div className="mb-3 flex items-start justify-between gap-3">
                        <div className="flex min-w-0 flex-1 gap-3">
                          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold bg-gray-100 text-gray-600">
                            {i + 1}
                          </span>
                          <div className="min-w-0">
                            <div className="text-[13.5px] font-semibold leading-snug text-gray-900">
                              {q.text}
                            </div>
                          </div>
                        </div>
                        <span className="shrink-0 whitespace-nowrap rounded-lg bg-blue-100 px-2 py-0.5 text-[11px] font-bold text-blue-700">
                          +{q.pts} {q.pts === 1 ? "pt" : "pts"}
                        </span>
                      </div>

                      <div className="grid grid-cols-2 gap-2">
                        <button
                          type="button"
                          className={`rounded-lg border py-2.5 text-[13px] font-semibold transition-all disabled:cursor-not-allowed disabled:opacity-60 ${
                            yes
                              ? "border-green-500 bg-green-100 text-green-800 shadow-sm"
                              : "border-gray-200 bg-white text-gray-700 hover:border-green-400 hover:bg-green-50 hover:text-green-800"
                          }`}
                          disabled={readOnly}
                          onClick={() => setAnswer(i, true)}
                        >
                          Yes
                        </button>
                        <button
                          type="button"
                          className={`rounded-lg border py-2.5 text-[13px] font-semibold transition-all disabled:cursor-not-allowed disabled:opacity-60 ${
                            no
                              ? "border-red-500 bg-red-100 text-red-800 shadow-sm"
                              : "border-gray-200 bg-white text-gray-700 hover:border-red-300 hover:bg-red-50 hover:text-red-700"
                          }`}
                          disabled={readOnly}
                          onClick={() => setAnswer(i, false)}
                        >
                          No
                        </button>
                      </div>

                      {showEvidence ? (
                        <div className="mt-3 rounded-lg border border-dashed border-purple-200 bg-purple-50/40 p-3">
                          <div className="mb-1.5 text-[11.5px] font-semibold text-gray-700">
                            {q.evidence_label}
                            {required && (
                              <span className="text-red-500 ml-0.5">*</span>
                            )}
                          </div>
                          {isDateEvidence ? (
                            <DatePicker
                              name={`poll1-evidence-${q.id}`}
                              value={val}
                              min={dateBounds?.min}
                              max={dateBounds?.max}
                              disabled={readOnly}
                              placeholder="MM/DD/YYYY"
                              className="max-w-[220px]"
                              error={dateError}
                              onChange={e => {
                                const next = e.target.value;
                                if (
                                  dateBounds?.min &&
                                  next &&
                                  next < dateBounds.min
                                ) {
                                  return;
                                }
                                if (
                                  dateBounds?.max &&
                                  next &&
                                  next > dateBounds.max
                                ) {
                                  return;
                                }
                                setEvidence(q.id, next);
                              }}
                            />
                          ) : (
                            <>
                              <input
                                type={inputType}
                                className={`w-full rounded-lg border px-2.5 py-1.5 text-[12.5px] transition-all focus:outline-none focus:ring-1 focus:ring-purple-200 ${
                                  missing
                                    ? "border-red-300 bg-red-50 focus:border-red-400"
                                    : "border-gray-200 bg-white focus:border-purple-400"
                                }`}
                                disabled={readOnly}
                                placeholder={q.evidence_placeholder || ""}
                                value={val}
                                onChange={e =>
                                  setEvidence(q.id, e.target.value)
                                }
                              />
                              {missing ? (
                                <div className="mt-1.5 text-[10.5px] font-semibold text-red-700">
                                  Required when answering Yes.
                                </div>
                              ) : null}
                            </>
                          )}
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>

              <div className="mt-4">
                <label
                  htmlFor="checkin-modal-notes"
                  className="mb-1.5 block text-xs font-semibold text-gray-700"
                >
                  Notes{" "}
                  <span className="font-normal text-gray-400">(optional)</span>
                </label>
                <textarea
                  id="checkin-modal-notes"
                  className="w-full min-h-[76px] resize-y rounded-lg border border-gray-200 px-3 py-2 text-[13px] transition-all focus:border-purple-400 focus:outline-none focus:ring-1 focus:ring-purple-100"
                  disabled={readOnly}
                  placeholder="Brief context for the team (e.g., who attended the QBR, when the on-site visit was, planned EBX date)."
                  value={notes}
                  onChange={e => onNotesChange(e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Right: Score / Progress sidebar */}
          <aside className="sticky top-0 flex flex-col gap-3.5 max-[860px]:static">
            {showRubric ? (
              <>
                <div className="rounded-xl border border-gray-200 bg-white py-4 shadow-sm flex flex-col items-center">
                  <CompositeDonut
                    composite={score}
                    periodLabel="My Score"
                    max={8}
                    tier={tierObj.tier}
                  />
                  <div className="mb-4 flex justify-center">
                    <TierPill tier={tierObj.tier} />
                  </div>
                </div>
                <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                  <div className="mb-3 text-[10px] font-bold uppercase tracking-widest text-gray-400">
                    Scoring Scale
                  </div>
                  {scoringScaleRows.map(row => (
                    <div
                      key={`${row.label}-${row.tier}`}
                      className="flex items-center justify-between gap-2 py-1 border-b border-gray-50 last:border-0"
                    >
                      <span className="text-xs text-gray-600">{row.label}</span>
                      <TierPill
                        tier={row.tier}
                        className="!text-[9px] !px-1.5 !py-0.5"
                      />
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <>
                <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm text-center">
                  <div className="mb-2 text-[10px] font-bold uppercase tracking-widest text-gray-400">
                    Progress
                  </div>
                  <div className="text-[44px] font-extrabold leading-none tracking-tight text-purple-600">
                    {answeredCount}
                    <span className="text-[22px] font-semibold text-gray-300">
                      {" "}
                      / {questions.length}
                    </span>
                  </div>
                  <div className={`mt-1 text-[11px] ${MCI_MUTED}`}>
                    Questions answered
                  </div>
                </div>
                <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                  <div className="mb-2.5 text-[10px] font-bold uppercase tracking-widest text-gray-400">
                    Scoring Rubric
                  </div>
                  <p className="m-0 text-xs leading-relaxed text-gray-500">
                    The points-per-question rubric is hidden by your program
                    admin. Answer truthfully; your tier will be communicated
                    after the leaderboard is published.
                  </p>
                </div>
              </>
            )}
          </aside>
        </div>
      </section>

      {/* ── Footer ── */}
      {!readOnly ? (
        <footer className="flex-shrink-0 flex items-center justify-between gap-4 border-t border-gray-200 pt-4 bg-white">
          <div className="min-w-0">
            {allAnswered && !evidenceOk ? (
              <p className="text-xs font-medium text-red-600">
                Add required evidence (and valid dates) to all &quot;Yes&quot;
                answers before submitting.
              </p>
            ) : !allAnswered ? (
              <p className="text-xs text-gray-400">
                {questions.length - answeredCount} question
                {questions.length - answeredCount !== 1 ? "s" : ""} remaining
              </p>
            ) : (
              <p className="text-xs text-green-600 font-medium">
                All questions answered — ready to submit.
              </p>
            )}
          </div>
          <div className="flex items-center gap-2.5 flex-shrink-0">
            <Button
              variant="secondary"
              onClick={onSaveDraft}
              disabled={submitting || savingDraft}
              loading={savingDraft}
            >
              Save Draft
            </Button>
            <Button
              variant="primary"
              onClick={onSubmit}
              disabled={!canSubmit}
              loading={submitting}
              title={
                allAnswered && !evidenceOk
                  ? "Add required evidence to submit"
                  : undefined
              }
            >
              Submit Check-In
            </Button>
          </div>
        </footer>
      ) : null}
    </CommonModal>
  );
};

export default CheckInModal;
